import React, { useState } from 'react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { HelpTooltip } from '@/components/HelpTooltip'
import HotelSectionHeader from '@/components/groups/HotelSectionHeader'
import { groupHotelCount, spansHotels } from '@/lib/reporting-scope'
import { groupRowsByHotel } from '@/lib/room-type-tree'
import type { ReportingScope } from '@/types/shared'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface RoomTypeOption {
  id: number
  name: string
  /** Which hotel this room is in (Groups) — two hotels legitimately share a room name. */
  propertyId?: number | null
  propertyName?: string | null
}

interface RatePlanOption {
  id: number
  name: string
  propertyId?: number | null
  propertyName?: string | null
}

interface OverrideFormProps {
  /** The property's backend-served canonical path. Every request target is this + a suffix. */
  propertyBasePath: string
  roomTypes: RoomTypeOption[]
  ratePlans: RatePlanOption[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Groups: the unit the blanket "All Room Types" option reaches, and its lead. */
  reportingScope?: ReportingScope | null
}

export default function OverrideForm({
  propertyBasePath,
  roomTypes,
  ratePlans,
  open,
  onOpenChange,
  reportingScope,
}: OverrideFormProps) {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [roomTypeId, setRoomTypeId] = useState<string>('all')
  const [ratePlanId, setRatePlanId] = useState<string>('all')
  const [rateAmount, setRateAmount] = useState('')
  const [stopSell, setStopSell] = useState(false)
  const [closedToArrival, setClosedToArrival] = useState(false)
  const [closedToDeparture, setClosedToDeparture] = useState(false)
  const [minStayArrival, setMinStayArrival] = useState('')
  const [maxStay, setMaxStay] = useState('')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const showHotels = spansHotels(roomTypes)

  // SECTIONED BY HOTEL, NOT BADGED PER ROW — the same correction the room editor's parent picker
  // needed. A chip beside every option pushed this panel to 546px inside a 512px dialog: it spilled
  // past the dialog's edge AND still truncated the hotel to "Modern Studios Close To Hyd…", so the
  // one thing the badge existed to say was the thing that got clipped. Said once per block, the room
  // name gets the width and the hotel name is readable. One hotel ⟹ no headings, i.e. the plain
  // list every ungrouped property has always had.
  const roomSections = groupRowsByHotel(roomTypes)
  const planSections = groupRowsByHotel(ratePlans)
  const isLeadHotel = (id?: number | null) =>
    reportingScope != null && id != null && id === reportingScope.leadPropertyId

  const blanketReach =
    roomTypeId === 'all' && showHotels && reportingScope
      ? `Applies to every room in this combined inventory, across all ${groupHotelCount(reportingScope)} listings. Pick a room to limit it to one.`
      : null

  function deriveOverrideType(): string {
    const hasRate = rateAmount !== ''
    const hasRestriction = stopSell || closedToArrival || closedToDeparture || minStayArrival !== '' || maxStay !== ''
    if (hasRate && hasRestriction) return 'both'
    if (hasRestriction) return 'restriction'
    return 'rate'
  }

  // The save goes through the server review first (change safety R1.5). The form steps aside for it
  // and keeps its fields, so "Back" reopens it exactly as it was (§5.17.1).
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const overrideType = deriveOverrideType()

    const data = {
      override: {
        start_date: startDate,
        end_date: endDate,
        room_type_id: roomTypeId === 'all' ? '' : roomTypeId,
        rate_plan_id: ratePlanId === 'all' ? '' : ratePlanId,
        override_type: overrideType,
        reason,
        ...(rateAmount ? { rate_amount: rateAmount } : {}),
        stop_sell: stopSell,
        closed_to_arrival: closedToArrival,
        closed_to_departure: closedToDeparture,
        ...(minStayArrival ? { min_stay_arrival: minStayArrival } : {}),
        ...(maxStay ? { max_stay: maxStay } : {}),
      },
    }

    onOpenChange(false)
    review.open(
      `${propertyBasePath}/overrides/review`,
      { operation: 'create', ...data },
      () =>
        inertiaSave('post', `${propertyBasePath}/overrides`, data, {
          onStart: () => setSaving(true),
          onFinish: () => setSaving(false),
        }),
      { onBack: () => onOpenChange(true) }
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Override</DialogTitle>
            <DialogDescription>
              Set a fixed rate or restriction that bypasses the rate engine.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-4">
              {/* Date range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="override-start">Start Date</Label>
                  <Input
                    id="override-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="override-end">End Date</Label>
                  <Input
                    id="override-end"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Room type + Rate plan */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="override-room-type">Room Type</Label>
                  <Select value={roomTypeId} onValueChange={setRoomTypeId}>
                    <SelectTrigger id="override-room-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px] max-w-[24rem]">
                      <SelectItem value="all" className="whitespace-normal break-words leading-snug">
                        All Room Types
                      </SelectItem>
                      {roomSections.map((section) => (
                        <SelectGroup key={section.propertyId ?? 'own'}>
                          {showHotels && section.propertyName && (
                            <SelectLabel className="mt-1 border-t border-border/40 px-2 pb-1.5 pt-2">
                              <HotelSectionHeader
                                name={section.propertyName}
                                propertyId={section.propertyId}
                                isLead={isLeadHotel(section.propertyId)}
                              />
                            </SelectLabel>
                          )}
                          {section.rows.map((rt) => (
                            <SelectItem
                              key={rt.id}
                              value={String(rt.id)}
                              className="whitespace-normal break-words leading-snug"
                            >
                              {rt.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* LOCKED RULE 9 — a blanket override with no room resolves against the room's own
                      hotel first, then the lead. One made HERE, at the lead, therefore reaches every
                      hotel in the unit. That is what managing from the lead means, and it is far too
                      consequential to leave the operator to infer from the words "All Room Types". */}
                  {blanketReach && <p className="text-[11px] text-amber-700">{blanketReach}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="override-rate-plan">Rate Plan</Label>
                  <Select value={ratePlanId} onValueChange={setRatePlanId}>
                    <SelectTrigger id="override-rate-plan">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px] max-w-[24rem]">
                      <SelectItem value="all" className="whitespace-normal break-words leading-snug">
                        All Rate Plans
                      </SelectItem>
                      {/* Two hotels legitimately hold a plan of the same NAME ("Bed and Breakfast"),
                          so a unit-wide plan list is ambiguous until it says whose it is — which is
                          what the hotel heading above each block does. */}
                      {planSections.map((section) => (
                        <SelectGroup key={section.propertyId ?? 'own'}>
                          {showHotels && section.propertyName && (
                            <SelectLabel className="mt-1 border-t border-border/40 px-2 pb-1.5 pt-2">
                              <HotelSectionHeader
                                name={section.propertyName}
                                propertyId={section.propertyId}
                                isLead={isLeadHotel(section.propertyId)}
                              />
                            </SelectLabel>
                          )}
                          {section.rows.map((rp) => (
                            <SelectItem
                              key={rp.id}
                              value={String(rp.id)}
                              className="whitespace-normal break-words leading-snug"
                            >
                              {rp.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rate amount */}
              <div className="space-y-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="override-rate">Fixed Rate</Label>
                  <HelpTooltip content="Optional. Sets the exact nightly price, replacing the computed rate entirely." />
                </div>
                <Input
                  id="override-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rateAmount}
                  onChange={(e) => setRateAmount(e.target.value)}
                  placeholder="Leave empty to keep computed rate"
                />
              </div>

              {/* Restrictions */}
              <div className="space-y-3 rounded-xl border border-border/60 p-4">
                <div className="flex items-center gap-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Booking Restrictions</p>
                  <HelpTooltip content="Optional. Control how guests can book this room on these dates. All restrictions are off by default." />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="override-stop-sell" className="text-sm cursor-pointer">Stop Sell</Label>
                    <p className="text-[11px] text-muted-foreground">Block room from being sold (maintenance, private use)</p>
                  </div>
                  <Switch id="override-stop-sell" checked={stopSell} onCheckedChange={setStopSell} />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="override-cta" className="text-sm cursor-pointer">Closed to Arrival</Label>
                    <p className="text-[11px] text-muted-foreground">Can't check in on this date</p>
                  </div>
                  <Switch id="override-cta" checked={closedToArrival} onCheckedChange={setClosedToArrival} />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="override-ctd" className="text-sm cursor-pointer">Closed to Departure</Label>
                    <p className="text-[11px] text-muted-foreground">Can't check out on this date</p>
                  </div>
                  <Switch id="override-ctd" checked={closedToDeparture} onCheckedChange={setClosedToDeparture} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="override-min-stay" className="text-sm">Min Stay</Label>
                    <Input
                      id="override-min-stay"
                      type="number"
                      min="1"
                      value={minStayArrival}
                      onChange={(e) => setMinStayArrival(e.target.value)}
                      placeholder="e.g. 2 nights"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="override-max-stay" className="text-sm">Max Stay</Label>
                    <Input
                      id="override-max-stay"
                      type="number"
                      min="1"
                      value={maxStay}
                      onChange={(e) => setMaxStay(e.target.value)}
                      placeholder="e.g. 14 nights"
                    />
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label htmlFor="override-reason">Reason</Label>
                <Input
                  id="override-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Bank holiday premium, maintenance"
                  required
                />
              </div>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-lg" disabled={saving}>
                {saving ? 'Creating...' : 'Create Override'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {review.dialog}
    </>
  )
}
